import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Hotel, Delete, Clock, LogIn, LogOut, UserRound, Coffee, BedDouble, ChevronLeft, ChevronDown, ChevronRight } from "lucide-react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "del"];

const HK_DEPTS = ["housekeeping", "houseman", "laundry", "executive"];

// Derive floor from a room number (101->"1", 228->"2", 1012->"10"). Client-side
// fallback so grouping works even if the API response omits `floor`.
const roomFloor = (number) => {
  const m = String(number || "").match(/^(\d+)/);
  if (!m) return "Other";
  const n = parseInt(m[1], 10);
  if (n >= 100) return String(Math.floor(n / 100));
  if (n >= 10) return String(Math.floor(n / 10));
  return "1";
};

const RSTATUS = {
  dirty: { label: "Dirty", dot: "bg-red-500", btn: "bg-red-500 hover:bg-red-600" },
  clean: { label: "Clean", dot: "bg-blue-500", btn: "bg-blue-500 hover:bg-blue-600" },
  inspected: { label: "Inspected", dot: "bg-emerald-500", btn: "bg-emerald-500 hover:bg-emerald-600" },
  ooo: { label: "Out of Order", dot: "bg-zinc-500", btn: "bg-zinc-600 hover:bg-zinc-700" },
};

const fmtT = (x, tz) => (x ? new Date(x).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }) : "");

const RESULT = {
  in: { ring: "bg-emerald-500/20 text-emerald-400", text: "text-emerald-400", label: "Clocked IN", icon: LogIn },
  out: { ring: "bg-amber-500/20 text-amber-400", text: "text-amber-400", label: "Clocked OUT", icon: LogOut },
  break_start: { ring: "bg-blue-500/20 text-blue-400", text: "text-blue-400", label: "Lunch started", icon: Coffee },
  break_end: { ring: "bg-blue-500/20 text-blue-400", text: "text-blue-400", label: "Back from lunch", icon: Coffee },
};

export default function Kiosk() {
  const { token } = useParams();
  const storeKey = `kiosk-creds-${token}`;
  const legacyKey = `kiosk-phone-${token}`;
  const [propertyName, setPropertyName] = useState("");
  const [tz, setTz] = useState(undefined);
  const [invalid, setInvalid] = useState(false);
  const [loginMode, setLoginMode] = useState("phone"); // "phone" | "ssn"
  const [phone, setPhone] = useState("");
  const [lastName, setLastName] = useState("");
  const [ssn4, setSsn4] = useState("");
  const [me, setMe] = useState(null); // {employee_id, name, first_name, department, status, ..., creds}
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [clock, setClock] = useState(new Date());
  const [roomsView, setRoomsView] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [selRoom, setSelRoom] = useState(null);
  const [collapsedFloors, setCollapsedFloors] = useState({});
  const [isExec, setIsExec] = useState(false);
  const [housekeepers, setHousekeepers] = useState([]);
  const [inspectNotes, setInspectNotes] = useState("");
  const [inspectHk, setInspectHk] = useState("");
  const [inspectRt, setInspectRt] = useState("");
  const [assignments, setAssignments] = useState({});
  const [assignMode, setAssignMode] = useState(false);
  const [assignHk, setAssignHk] = useState("");
  const [checkedRooms, setCheckedRooms] = useState({});
  const [roomTypes, setRoomTypes] = useState({});
  const toggleFloor = (f) => setCollapsedFloors((c) => ({ ...c, [f]: !c[f] }));
  const [pickPosition, setPickPosition] = useState(false);
  const [hoursView, setHoursView] = useState(false);
  const [hoursData, setHoursData] = useState(null);
  const [hoursBusy, setHoursBusy] = useState(false);
  const [showHandbook, setShowHandbook] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [signName, setSignName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [station, setStation] = useState(() => localStorage.getItem(`kiosk-station-${token}`) === "1");

  const fileBase = `${process.env.REACT_APP_BACKEND_URL}/api/kiosk/${token}`;

  const toggleStation = () => {
    setStation((s) => {
      const next = !s;
      localStorage.setItem(`kiosk-station-${token}`, next ? "1" : "0");
      return next;
    });
  };

  const getCoords = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve({}), // denied / unavailable → backend flags as off-site for review
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  });

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const identify = useCallback(async (creds, remember) => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/kiosk/${token}/identify`, creds);
      setMe({ ...r.data, creds });
      if (remember) localStorage.setItem(storeKey, JSON.stringify(creds));
      setPhone("");
      setLastName("");
      setSsn4("");
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      return false;
    } finally {
      setBusy(false);
    }
  }, [token, storeKey]);

  useEffect(() => {
    api.get(`/kiosk/${token}`)
      .then((r) => {
        setPropertyName(r.data.property_name);
        setTz(r.data.timezone);
        const saved = localStorage.getItem(storeKey);
        if (saved) {
          try { identify(JSON.parse(saved), false); } catch { /* ignore */ }
        } else {
          const legacy = localStorage.getItem(legacyKey);
          if (legacy) identify({ phone: legacy }, false);
        }
      })
      .catch(() => setInvalid(true));
  }, [token, storeKey, legacyKey, identify]);

  const press = (k) => {
    setError("");
    if (k === "clear") return setPhone("");
    if (k === "del") return setPhone((p) => p.slice(0, -1));
    if (phone.replace(/\D/g, "").length >= 15) return;
    setPhone((p) => p + k);
  };

  const fmtPhone = (p) => {
    const d = p.replace(/\D/g, "");
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}${d.slice(10) ? " " + d.slice(10) : ""}`;
  };

  const submitPhone = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) return setError("Please enter your phone number");
    await identify({ phone: digits }, true);
  };

  const submitSsn = async () => {
    const ln = lastName.trim();
    const s4 = ssn4.replace(/\D/g, "");
    if (!ln) return setError("Please enter your last name");
    if (s4.length !== 4) return setError("Enter the last 4 digits of your SSN");
    await identify({ last_name: ln, ssn_last4: s4 }, true);
  };

  const doAction = useCallback(async (kind, extra = {}) => {
    if (!me) return;
    setBusy(true);
    setError("");
    try {
      let payload = { ...me.creds, ...extra };
      // location-based clock-in (personal phones only; a front-desk station is exempt)
      if (kind === "punch" && me.status === "out") {
        if (station) {
          payload.station = true;
        } else if (me.geofence_enabled) {
          payload = { ...payload, ...(await getCoords()) };
        }
      }
      const r = await api.post(`/kiosk/${token}/${kind}`, payload);
      if (r.data?.action === "handbook_required") {
        setShowHandbook(true);
        setPickPosition(false);
        return;
      }
      if (r.data?.action === "reviews_required") {
        setPendingReviews(r.data.pending_reviews || []);
        setReviewIdx(0);
        setShowReviews(true);
        setPickPosition(false);
        setAgreed(false);
        setSignName("");
        return;
      }
      setResult(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }, [me, token, station]);

  const submitAck = async () => {
    if (!me?.handbook) return;
    if (signName.trim().length < 2) return setError("Please type your full name to sign");
    if (!agreed) return setError("Please check the box to acknowledge");
    setBusy(true);
    setError("");
    try {
      await api.post(`/kiosk/${token}/handbook/acknowledge`, {
        employee_id: me.employee_id, typed_name: signName.trim(), agreed: true,
      });
      const fresh = await api.post(`/kiosk/${token}/identify`, me.creds);
      setMe({ ...fresh.data, creds: me.creds });
      setShowHandbook(false);
      setAgreed(false);
      setSignName("");
      // continue to clock in
      if ((fresh.data?.positions?.length || 0) > 1) setPickPosition(true);
      else doAction("punch");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const clockIn = () => {
    if (me?.handbook_required) return setShowHandbook(true);
    if ((me?.positions?.length || 0) > 1) setPickPosition(true);
    else doAction("punch");
  };

  const submitReviewAck = async () => {
    const rev = pendingReviews[reviewIdx];
    if (!rev) return;
    if (signName.trim().length < 2) return setError("Please type your full name to sign");
    if (!agreed) return setError("Please check the box to acknowledge");
    setBusy(true);
    setError("");
    try {
      await api.post(`/kiosk/${token}/reviews/${rev.id}/acknowledge`, {
        employee_id: me.employee_id, typed_name: signName.trim(), agreed: true,
      });
      setSignName("");
      setAgreed(false);
      if (reviewIdx + 1 < pendingReviews.length) {
        setReviewIdx(reviewIdx + 1);
      } else {
        setShowReviews(false);
        setPendingReviews([]);
        setReviewIdx(0);
        // all reviews signed — continue clock in (pick position first if multiple)
        if ((me?.positions?.length || 0) > 1) setPickPosition(true);
        else doAction("punch");
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const backToWelcome = () => {
    setResult(null);
    setError("");
    setPickPosition(false);
    setShowHandbook(false);
    setShowReviews(false);
    setPendingReviews([]);
    setReviewIdx(0);
    if (me) identify(me.creds, false); // refresh status
  };

  const viewHours = async () => {
    if (!me) return;
    setHoursBusy(true);
    try {
      const r = await api.get(`/kiosk/${token}/timesheet`, { params: { employee_id: me.employee_id } });
      setHoursData(r.data);
      setHoursView(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your hours");
    } finally {
      setHoursBusy(false);
    }
  };

  const switchUser = () => {
    localStorage.removeItem(storeKey);
    localStorage.removeItem(legacyKey);
    setMe(null);
    setResult(null);
    setError("");
    setPhone("");
    setLastName("");
    setSsn4("");
    setLoginMode("phone");
    setRoomsView(false);
    setSelRoom(null);
    setPickPosition(false);
    setShowHandbook(false);
    setAgreed(false);
    setSignName("");
  };

  const openRooms = useCallback(async (mode = false) => {
    if (!me) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/kiosk/${token}/rooms`, me.creds);
      setRooms(r.data.rooms || []);
      setIsExec(!!r.data.is_exec);
      setHousekeepers(r.data.housekeepers || []);
      setAssignments(r.data.assignments || {});
      setRoomTypes(Object.fromEntries((r.data.rooms || []).map((rm) => [rm.number, rm.assigned_type || "stayover"])));
      setCheckedRooms({});
      setAssignMode(!!mode);
      setSelRoom(null);
      setRoomsView(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }, [me, token]);

  const assignRoomKiosk = async (number, employee_id, room_type) => {
    await api.post(`/kiosk/${token}/assign`, { ...me.creds, number, employee_id: employee_id || null, room_type: room_type || null });
    const hk = housekeepers.find((h) => h.id === employee_id);
    const name = employee_id ? (hk?.name || "") : "";
    setRooms((rs) => rs.map((x) => (x.number === number ? { ...x, assigned_to_id: employee_id || "", assigned_to: name, assigned_type: employee_id ? (room_type || "stayover") : "" } : x)));
    setAssignments((a) => {
      const next = { ...a };
      if (employee_id) next[number] = { employee_id, employee_name: name, room_type: room_type || "stayover" };
      else delete next[number];
      return next;
    });
  };

  const bulkAssign = async () => {
    if (!assignHk) { setError("Pick a housekeeper at the top first."); return; }
    const nums = Object.keys(checkedRooms).filter((n) => checkedRooms[n]);
    if (nums.length === 0) { setError("Check at least one room to assign."); return; }
    setBusy(true);
    setError("");
    try {
      const empId = assignHk === "unassign" ? "" : assignHk;
      for (const n of nums) {
        await assignRoomKiosk(n, empId, roomTypes[n] || "stayover");
      }
      setCheckedRooms({});
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setRoomTypeLocal = async (number, rt) => {
    setRoomTypes((m) => ({ ...m, [number]: rt }));
    // If the room is already assigned, persist the type change immediately.
    const cur = rooms.find((r) => r.number === number);
    if (cur?.assigned_to_id) {
      setBusy(true);
      try { await assignRoomKiosk(number, cur.assigned_to_id, rt); }
      catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setBusy(false); }
    }
  };

  const setRoomStatus = async (number, status, room_type = "") => {
    setBusy(true);
    try {
      await api.post(`/kiosk/${token}/room-status`, { ...me.creds, number, status, room_type });
      setRooms((rs) => rs.map((x) => (x.number === number ? { ...x, status } : x)));
      setSelRoom(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openRoom = (r) => { setSelRoom(r); setInspectNotes(r.notes || ""); setInspectHk(r.cleaned_by_id || r.assigned_to_id || ""); setInspectRt(r.room_type || r.assigned_type || ""); setError(""); };

  const inspectRoom = async (number) => {
    setBusy(true);
    try {
      await api.post(`/kiosk/${token}/room-status`, { ...me.creds, number, status: "inspected", notes: inspectNotes, cleaned_by_id: inspectHk || null, room_type: inspectRt || selRoom?.room_type || "" });
      setSelRoom(null);
      await openRooms();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const failRoom = async (number) => {
    if (!inspectNotes.trim()) { setError("Add a note so the housekeeper knows what to fix."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post(`/kiosk/${token}/room-status`, { ...me.creds, number, status: "dirty", notes: inspectNotes });
      setSelRoom(null);
      await openRooms();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const hkDept = (d) => HK_DEPTS.includes((d || "").toLowerCase());
  const isHK = me && (hkDept(me.current_position) || (me.positions || []).some((p) => hkDept(p.department)));
  const isExecMe = me && (
    (me.department || "").toLowerCase().includes("executive") ||
    (me.current_position || "").toLowerCase().includes("executive") ||
    (me.positions || []).some((p) => (p.department || "").toLowerCase().includes("executive"))
  );

  useEffect(() => {
    if (!result) return;
    const t = setTimeout(backToWelcome, 5000);
    return () => clearTimeout(t);
  }, [result]);

  if (invalid) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-neutral-950 text-white">
        <Hotel className="mb-4 h-10 w-10 text-neutral-500" />
        <div className="text-xl font-semibold">Invalid kiosk link</div>
        <p className="mt-2 text-sm text-neutral-400">Please ask your manager for the current Time Clock QR code.</p>
      </div>
    );
  }

  const timeStr = clock.toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", second: "2-digit" });
  const dateStr = clock.toLocaleDateString("en-US", { timeZone: tz, weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="flex min-h-screen flex-col items-center bg-neutral-950 px-4 py-8 text-white" data-testid="kiosk-page">
      {hoursView && hoursData && (
        <div className="fixed inset-0 z-50 flex flex-col bg-neutral-950/95 p-4 sm:p-8" data-testid="kiosk-hours-overlay">
          <div className="mx-auto flex w-full max-w-lg flex-1 flex-col overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
            <div className="flex items-center justify-between border-b border-neutral-800 px-5 py-4">
              <div>
                <div className="text-lg font-black">My Hours</div>
                <div className="text-xs text-neutral-400">{hoursData.name}</div>
              </div>
              <Button onClick={() => setHoursView(false)} data-testid="kiosk-hours-close" className="bg-neutral-800 text-white hover:bg-neutral-700">Close</Button>
            </div>
            <div className="flex-1 overflow-auto px-5 py-4">
              {["current", "previous"].map((key) => {
                const p = hoursData[key];
                if (!p) return null;
                return (
                  <div key={key} className="mb-6">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-bold uppercase tracking-wide text-[#8aa0ff]">
                        {key === "current" ? "Current pay period" : "Last pay period"}
                      </div>
                      <div className="text-xs text-neutral-400">{p.start} → {p.end}</div>
                    </div>
                    {p.days.length === 0 ? (
                      <div className="rounded-md border border-neutral-800 px-4 py-6 text-center text-sm text-neutral-500">No hours recorded.</div>
                    ) : (
                      <div className="overflow-hidden rounded-md border border-neutral-800">
                        {p.days.map((d) => (
                          <div key={d.date} className="flex items-center justify-between border-b border-neutral-800 px-4 py-2.5 last:border-b-0" data-testid={`kiosk-hours-day-${d.date}`}>
                            <div className="text-sm">
                              <div className="font-semibold">{new Date(d.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
                              {d.open ? (
                                <span className="text-xs text-emerald-400">Currently clocked in</span>
                              ) : d.approved ? (
                                <span className="text-xs text-emerald-400">✓ Approved</span>
                              ) : (
                                <span className="text-xs font-semibold text-amber-400">Not approved yet</span>
                              )}
                            </div>
                            <div className="font-mono text-base font-bold tabular-nums">{d.hours.toFixed(2)} <span className="text-xs font-normal text-neutral-500">hrs</span></div>
                          </div>
                        ))}
                        <div className="flex items-center justify-between bg-neutral-800/60 px-4 py-2.5">
                          <div className="text-sm font-bold uppercase tracking-wide">Total</div>
                          <div className="font-mono text-lg font-black tabular-nums text-emerald-400" data-testid={`kiosk-hours-total-${key}`}>{p.total.toFixed(2)} hrs</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-center text-xs text-neutral-500">Days marked <span className="font-semibold text-amber-400">Not approved yet</span> are awaiting your manager's approval. Please tell your manager if any hours look wrong.</p>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center bg-[#0033ff]">
          <Hotel className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <div className="text-lg font-black tracking-tight">{propertyName || "…"}</div>
          <div className="text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500">Time Clock</div>
        </div>
      </div>

      <div className="mt-6 text-center">
        <div className="font-mono text-5xl font-bold tabular-nums" data-testid="kiosk-clock">{timeStr}</div>
        <div className="mt-1 text-sm text-neutral-400">{dateStr}</div>
      </div>

      <div className="mt-8 w-full max-w-sm">
        {result ? (() => {
          const rc = RESULT[result.action] || RESULT.in;
          const Icon = rc.icon;
          return (
            <div className="flex flex-col items-center rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center" data-testid="kiosk-result">
              <div className={`flex h-16 w-16 items-center justify-center rounded-full ${rc.ring}`}>
                <Icon className="h-8 w-8" />
              </div>
              <div className="mt-4 text-2xl font-bold">{result.name}</div>
              <div className={`mt-1 text-lg font-semibold ${rc.text}`}>{rc.label}</div>
              <div className="mt-1 text-sm text-neutral-400">
                at {fmtT(result.time, tz)}
                {result.action === "in" && result.position ? ` · as ${result.position}` : ""}
                {result.action === "out" && result.hours != null ? ` · ${result.hours} hrs worked` : ""}
                {result.action === "break_end" && result.break_minutes != null ? ` · ${result.break_minutes} min lunch total` : ""}
              </div>
              {result.period_hours != null && (result.action === "in" || result.action === "out") && (
                <div className="mt-3 rounded-md bg-emerald-500/10 px-4 py-2 text-sm font-semibold text-emerald-400" data-testid="kiosk-result-period-hours">
                  {result.period_hours} hrs this pay period
                  {result.last_period_hours != null && <span className="ml-2 text-neutral-500">· {result.last_period_hours} last period</span>}
                </div>
              )}
              {result.offsite && (
                <div className="mt-3 rounded-md bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-400" data-testid="kiosk-result-offsite">
                  Clocked in off-site — flagged for your manager to review
                </div>
              )}
              <Button variant="secondary" className="mt-5" onClick={backToWelcome} data-testid="kiosk-done-btn">Done</Button>
            </div>
          );
        })() : me && showHandbook ? (
          /* ---- employee handbook acknowledgement (required before first clock-in) ---- */
          <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6" data-testid="kiosk-handbook">
            <div className="text-center">
              <div className="text-sm text-neutral-400">Before you clock in</div>
              <div className="text-xl font-bold">{me.handbook?.title || "Employee Handbook"}</div>
              <div className="text-xs text-neutral-500">Please review and sign to acknowledge you have read it.</div>
            </div>
            {me.handbook?.id && (
              <iframe
                title="Employee Handbook"
                src={`${fileBase}/handbook/${me.handbook.id}/file`}
                className="mt-4 h-72 w-full rounded border border-neutral-800 bg-white"
                data-testid="kiosk-handbook-pdf"
              />
            )}
            <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm text-neutral-300">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-5 w-5 accent-emerald-500" data-testid="kiosk-handbook-agree" />
              <span>I acknowledge that I have received, read, and understand the Employee Handbook.</span>
            </label>
            <Input value={signName} onChange={(e) => setSignName(e.target.value)}
              placeholder="Type your full name to sign"
              className="mt-3 h-12 border-neutral-700 bg-neutral-950 text-center text-lg"
              data-testid="kiosk-handbook-signature" />
            {error && <div className="mt-3 rounded bg-red-500/15 px-3 py-2 text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
            <Button onClick={submitAck} disabled={busy}
              className="mt-4 h-14 w-full bg-emerald-500 text-lg font-bold text-black hover:bg-emerald-400"
              data-testid="kiosk-handbook-submit">
              {busy ? "Please wait…" : "Agree & Clock In"}
            </Button>
            <button onClick={() => { setShowHandbook(false); setError(""); }}
              className="mt-4 block w-full text-center text-xs text-neutral-500 underline hover:text-neutral-300"
              data-testid="kiosk-handbook-cancel">Cancel</button>
          </div>
        ) : me && showReviews ? (
          /* ---- manager review acknowledgement (required before clock-in) ---- */
          (() => {
            const rev = pendingReviews[reviewIdx];
            return (
              <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6" data-testid="kiosk-review">
                <div className="text-center">
                  <div className="text-sm text-neutral-400">Message from management — please read</div>
                  <div className="mt-1 text-xl font-bold" data-testid="kiosk-review-title">{rev?.title}</div>
                  {pendingReviews.length > 1 && (
                    <div className="text-xs text-neutral-500">{reviewIdx + 1} of {pendingReviews.length}</div>
                  )}
                </div>
                <div className="mt-4 max-h-72 overflow-y-auto whitespace-pre-wrap rounded border border-neutral-800 bg-neutral-950 p-4 text-sm text-neutral-200" data-testid="kiosk-review-message">
                  {rev?.message}
                </div>
                <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm text-neutral-300">
                  <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1 h-5 w-5 accent-emerald-500" data-testid="kiosk-review-agree" />
                  <span>I acknowledge that I have read and understand this review.</span>
                </label>
                <Input value={signName} onChange={(e) => setSignName(e.target.value)}
                  placeholder="Type your full name to sign"
                  className="mt-3 h-12 border-neutral-700 bg-neutral-950 text-center text-lg"
                  data-testid="kiosk-review-signature" />
                {error && <div className="mt-3 rounded bg-red-500/15 px-3 py-2 text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
                <Button onClick={submitReviewAck} disabled={busy}
                  className="mt-4 h-14 w-full bg-emerald-500 text-lg font-bold text-black hover:bg-emerald-400"
                  data-testid="kiosk-review-submit">
                  {busy ? "Please wait…" : (reviewIdx + 1 < pendingReviews.length ? "Acknowledge & Next" : "Acknowledge & Clock In")}
                </Button>
                <button onClick={() => { setShowReviews(false); setError(""); }}
                  className="mt-4 block w-full text-center text-xs text-neutral-500 underline hover:text-neutral-300"
                  data-testid="kiosk-review-cancel">Cancel</button>
              </div>
            );
          })()
        ) : me && roomsView ? (
          /* ---- housekeeper room updates ---- */
          <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4" data-testid="kiosk-rooms">
            <div className="mb-3 flex items-center gap-2">
              <button onClick={() => { setRoomsView(false); setSelRoom(null); setAssignMode(false); setError(""); }} className="flex items-center gap-1 text-sm text-neutral-400 hover:text-white" data-testid="kiosk-rooms-back">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              <span className="ml-1 text-sm font-semibold">{assignMode ? "Assign Rooms" : "Room Status"}</span>
            </div>
            {assignMode ? (
              <div data-testid="kiosk-assign">
                <div className="sticky top-0 z-10 -mx-4 mb-3 border-b border-neutral-800 bg-neutral-900 px-4 pb-3">
                  <label className="text-[0.65rem] uppercase tracking-wide text-neutral-400">Assign to housekeeper</label>
                  <select value={assignHk} onChange={(e) => setAssignHk(e.target.value)} data-testid="kiosk-assign-hk"
                    className="mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-800 p-2.5 text-sm text-white">
                    <option value="">— pick a housekeeper —</option>
                    {housekeepers.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                    <option value="unassign">— Unassign (clear) —</option>
                  </select>
                  <Button disabled={busy} onClick={bulkAssign} data-testid="kiosk-assign-apply"
                    className="mt-2 h-11 w-full bg-emerald-500 text-sm font-bold text-black hover:bg-emerald-400">
                    {assignHk === "unassign" ? "Unassign" : "Assign"} {Object.values(checkedRooms).filter(Boolean).length} selected
                  </Button>
                  {error && <div className="mt-2 rounded bg-red-500/15 px-3 py-2 text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
                </div>
                {rooms.length === 0 ? (
                  <p className="py-8 text-center text-sm text-neutral-500">No rooms set up yet. Ask your manager to add room numbers to the property.</p>
                ) : (
                  <div className="max-h-[52vh] space-y-3 overflow-y-auto pr-1">
                    {Object.entries(rooms.reduce((acc, r) => {
                      const f = r.floor || roomFloor(r.number);
                      (acc[f] = acc[f] || []).push(r);
                      return acc;
                    }, {}))
                      .sort((a, b) => (parseInt(a[0], 10) || 999) - (parseInt(b[0], 10) || 999))
                      .map(([floor, frooms]) => {
                        const assignedCount = frooms.filter((r) => r.assigned_to_id).length;
                        return (
                          <div key={floor} className="rounded-lg border border-neutral-800">
                            <div className="flex items-center justify-between px-3 py-2 text-sm font-bold text-neutral-200">
                              <span>{floor === "Other" ? "Other rooms" : `Floor ${floor}`}</span>
                              <span className="text-[0.6rem] font-normal text-neutral-500">{assignedCount}/{frooms.length} assigned</span>
                            </div>
                            <div className="space-y-2 border-t border-neutral-800 p-2">
                              {frooms.map((r) => {
                                const rt = roomTypes[r.number] || "stayover";
                                const checked = !!checkedRooms[r.number];
                                return (
                                  <div key={r.number} className={`flex items-center gap-2 rounded-lg border p-2 ${checked ? "border-emerald-500/60 bg-emerald-500/10" : "border-neutral-800"}`} data-testid={`kiosk-assign-row-${r.number}`}>
                                    <input type="checkbox" checked={checked} disabled={busy}
                                      onChange={(e) => setCheckedRooms((c) => ({ ...c, [r.number]: e.target.checked }))}
                                      data-testid={`kiosk-assign-check-${r.number}`}
                                      className="h-5 w-5 shrink-0 accent-emerald-500" />
                                    <div className="min-w-0 flex-1">
                                      <div className="text-base font-bold text-white">{r.number}</div>
                                      {r.assigned_to && <div className="truncate text-[0.6rem] text-amber-300">→ {r.assigned_to} · {r.assigned_type === "checkout" ? "C/O" : "Stay"}</div>}
                                    </div>
                                    <div className="flex shrink-0 overflow-hidden rounded-lg border border-neutral-700">
                                      <button type="button" disabled={busy} onClick={() => setRoomTypeLocal(r.number, "stayover")}
                                        data-testid={`kiosk-assign-stay-${r.number}`}
                                        className={`px-2.5 py-1.5 text-xs font-bold ${rt === "stayover" ? "bg-blue-500 text-white" : "bg-neutral-800 text-neutral-400"}`}>Stay</button>
                                      <button type="button" disabled={busy} onClick={() => setRoomTypeLocal(r.number, "checkout")}
                                        data-testid={`kiosk-assign-co-${r.number}`}
                                        className={`px-2.5 py-1.5 text-xs font-bold ${rt === "checkout" ? "bg-indigo-500 text-white" : "bg-neutral-800 text-neutral-400"}`}>C/O</button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            ) : selRoom ? (
              <div className="text-center" data-testid="kiosk-room-chooser">
                <div className="text-sm text-neutral-400">Room</div>
                <div className="text-3xl font-black">{selRoom.number}</div>
                {selRoom.assigned_to && <div className="mt-1 text-xs font-bold text-amber-300" data-testid="kiosk-room-assigned">Assigned to {selRoom.assigned_to}{selRoom.assigned_type ? ` · ${selRoom.assigned_type === "checkout" ? "Checkout" : "Stayover"}` : ""}</div>}
                {selRoom.notes && <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300" data-testid="kiosk-room-note">⚠ {selRoom.notes}</div>}
                <div className="mt-5">
                  <div className="mb-2 text-xs uppercase tracking-wide text-neutral-500">Mark cleaned as</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button disabled={busy} onClick={() => setRoomStatus(selRoom.number, "clean", "stayover")} data-testid="kiosk-room-set-stayover"
                      className={`h-20 w-full flex-col bg-blue-500 text-base font-bold text-white hover:bg-blue-600 ${(selRoom.cleaned_today ? selRoom.room_type : selRoom.assigned_type) === "stayover" ? "ring-4 ring-white ring-offset-2 ring-offset-neutral-900" : ""}`}>
                      Stayover
                      <span className="mt-0.5 text-[0.6rem] font-normal opacity-80">Guest staying</span>
                    </Button>
                    <Button disabled={busy} onClick={() => setRoomStatus(selRoom.number, "clean", "checkout")} data-testid="kiosk-room-set-checkout"
                      className={`h-20 w-full flex-col bg-indigo-500 text-base font-bold text-white hover:bg-indigo-600 ${(selRoom.cleaned_today ? selRoom.room_type : selRoom.assigned_type) === "checkout" ? "ring-4 ring-white ring-offset-2 ring-offset-neutral-900" : ""}`}>
                      Checkout
                      <span className="mt-0.5 text-[0.6rem] font-normal opacity-80">Guest departed</span>
                    </Button>
                  </div>
                  <Button disabled={busy} onClick={() => setRoomStatus(selRoom.number, "ooo")} data-testid="kiosk-room-set-ooo"
                    variant="outline" className={`mt-2 h-12 w-full border-neutral-700 bg-transparent text-neutral-200 hover:bg-neutral-800 hover:text-white ${selRoom.status === "ooo" ? "ring-2 ring-zinc-400" : ""}`}>
                    Out of Order
                  </Button>
                  {(selRoom.cleaned_today || selRoom.status === "clean" || selRoom.status === "ooo" || selRoom.inspected_today) && (
                    <Button disabled={busy} onClick={() => setRoomStatus(selRoom.number, "dirty")} data-testid="kiosk-room-set-dirty"
                      variant="outline" className="mt-2 h-12 w-full border-amber-600/50 bg-transparent text-amber-300 hover:bg-amber-500/10 hover:text-amber-200">
                      Undo — Mark Not Cleaned (Dirty)
                    </Button>
                  )}
                </div>
                {isExec && (
                  <div className="mt-5 border-t border-neutral-800 pt-4 text-left" data-testid="kiosk-exec-inspect">
                    <div className="mb-2 text-xs uppercase tracking-wide text-emerald-400">Executive inspection</div>
                    {selRoom.cleaned_today && selRoom.cleaned_by && (
                      <div className="mb-2 text-xs text-blue-300">🧹 Cleaned by {selRoom.cleaned_by}{selRoom.room_type ? ` · ${selRoom.room_type === "checkout" ? "C/O" : "Stay"}` : ""}</div>
                    )}
                    {selRoom.inspected_today && (
                      <div className="mb-2 text-xs text-emerald-300">✓ Already inspected by {selRoom.inspected_by} — you can update it below</div>
                    )}
                    <label className="text-[0.65rem] text-neutral-400">Room type</label>
                    <div className="mb-3 mt-1 grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setInspectRt("stayover")} data-testid="kiosk-inspect-stayover"
                        className={`rounded-lg border p-2.5 text-sm font-bold ${inspectRt === "stayover" ? "border-blue-400 bg-blue-500/30 text-white ring-2 ring-blue-300" : "border-neutral-700 bg-neutral-800 text-neutral-300"}`}>Stayover</button>
                      <button type="button" onClick={() => setInspectRt("checkout")} data-testid="kiosk-inspect-checkout"
                        className={`rounded-lg border p-2.5 text-sm font-bold ${inspectRt === "checkout" ? "border-indigo-400 bg-indigo-500/30 text-white ring-2 ring-indigo-300" : "border-neutral-700 bg-neutral-800 text-neutral-300"}`}>Checkout</button>
                    </div>
                    <label className="text-[0.65rem] text-neutral-400">Cleaned by (correct if needed)</label>
                    <select value={inspectHk} onChange={(e) => setInspectHk(e.target.value)} data-testid="kiosk-inspect-hk"
                      className="mb-3 mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-800 p-2.5 text-sm text-white">
                      <option value="">— unassigned —</option>
                      {housekeepers.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                    </select>
                    <label className="text-[0.65rem] text-neutral-400">Inspection note (required to send back)</label>
                    <textarea value={inspectNotes} onChange={(e) => setInspectNotes(e.target.value)} rows={2} data-testid="kiosk-inspect-notes"
                      placeholder="Add a comment for this room…"
                      className="mb-3 mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-800 p-2.5 text-sm text-white" />
                    <Button disabled={busy} onClick={() => inspectRoom(selRoom.number)} data-testid="kiosk-room-inspect"
                      className="h-14 w-full bg-emerald-500 text-base font-bold text-black hover:bg-emerald-400">
                      {selRoom.inspected_today ? "Update Inspection" : "Inspected — Ready to Sell"}
                    </Button>
                    {error && <div className="mt-2 rounded bg-red-500/15 px-3 py-2 text-sm text-red-400" data-testid="kiosk-inspect-error">{error}</div>}
                    <Button disabled={busy} onClick={() => failRoom(selRoom.number)} data-testid="kiosk-room-fail"
                      className="mt-2 h-12 w-full bg-red-600 text-base font-bold text-white hover:bg-red-500">
                      Send Back — Needs Redo
                    </Button>
                    <p className="mt-2 text-center text-[0.6rem] text-neutral-500">Sends the room back to Dirty with your note so the housekeeper knows to redo it.</p>
                  </div>
                )}
                <button onClick={() => setSelRoom(null)} className="mt-4 text-xs text-neutral-500 underline">Cancel</button>
              </div>
            ) : (
              <>
                {isExec && rooms.length > 0 && (() => {
                  const ready = rooms.filter((r) => r.status === "clean" && !r.inspected_today);
                  return (
                    <div className="mb-3 rounded-lg border border-emerald-700/40 bg-emerald-500/5 p-3" data-testid="kiosk-ready-inspect">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide text-emerald-400">Ready for inspection</span>
                        <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.6rem] font-semibold text-emerald-400" data-testid="kiosk-ready-count">{ready.length}</span>
                      </div>
                      {ready.length === 0 ? (
                        <p className="py-2 text-center text-xs text-neutral-500">No cleaned rooms waiting. Rooms appear here once housekeepers mark them clean.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {ready.map((r) => (
                            <button key={r.number} onClick={() => openRoom(r)} data-testid={`kiosk-ready-room-${r.number}`}
                              className="flex flex-col items-center rounded-lg border border-emerald-700/50 bg-emerald-500/10 p-2 hover:bg-emerald-500/20">
                              <span className="text-base font-bold text-white">{r.number}</span>
                              <span className="mt-0.5 text-[0.55rem] font-semibold text-emerald-300">{r.room_type === "checkout" ? "C/O" : r.room_type === "stayover" ? "Stay" : "Clean"}</span>
                              {r.cleaned_by && <span className="mt-0.5 line-clamp-1 text-[0.55rem] text-neutral-400">{r.cleaned_by}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {rooms.length === 0 ? (
                  <p className="py-8 text-center text-sm text-neutral-500">No rooms set up yet. Ask your manager to add room numbers to the property.</p>
                ) : (
                  <div className="max-h-[52vh] space-y-3 overflow-y-auto pr-1">
                    {Object.entries(rooms.reduce((acc, r) => {
                      const f = r.floor || roomFloor(r.number);
                      (acc[f] = acc[f] || []).push(r);
                      return acc;
                    }, {}))
                      .sort((a, b) => (parseInt(a[0], 10) || 999) - (parseInt(b[0], 10) || 999))
                      .map(([floor, frooms]) => {
                        const collapsed = collapsedFloors[floor];
                        const remaining = frooms.filter((r) => r.status !== "inspected").length;
                        return (
                          <div key={floor} className="rounded-lg border border-neutral-800">
                            <button onClick={() => toggleFloor(floor)} data-testid={`kiosk-floor-${floor}`}
                              className="flex w-full items-center justify-between px-3 py-2.5 text-left">
                              <span className="flex items-center gap-2 text-sm font-bold text-neutral-200">
                                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                {floor === "Other" ? "Other rooms" : `Floor ${floor}`}
                                <span className="text-[0.65rem] font-normal text-neutral-500">({frooms.length} rooms)</span>
                              </span>
                              {remaining > 0 && (
                                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[0.6rem] font-semibold text-amber-400">{remaining} to do</span>
                              )}
                            </button>
                            {!collapsed && (
                              <div className="grid grid-cols-3 gap-2 border-t border-neutral-800 p-2">
                                {frooms.map((r) => {
                                  const st = RSTATUS[r.status];
                                  return (
                                    <button key={r.number} onClick={() => openRoom(r)} data-testid={`kiosk-room-${r.number}`}
                                      className={`flex flex-col items-center rounded-lg border p-2 ${r.notes ? "border-amber-500/60 bg-amber-500/10" : "border-neutral-800 bg-neutral-800/50"}`}>
                                      <span className="text-base font-bold">{r.number}</span>
                                      <span className="mt-1 flex items-center gap-1 text-[0.6rem] text-neutral-400">
                                        {st ? <><span className={`h-2 w-2 rounded-full ${st.dot}`} />{st.label}</> : <span className="text-neutral-600">tap to update</span>}
                                      </span>
                                      {r.notes && <span className="mt-1 line-clamp-2 text-[0.6rem] font-semibold text-amber-400">⚠ {r.notes}</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
                <p className="mt-3 text-center text-[0.65rem] text-neutral-600">Tap a floor, then the room you finished, and choose Stayover or Checkout.</p>
              </>
            )}
            {error && <div className="mt-3 rounded bg-red-500/15 px-3 py-2 text-center text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
          </div>
        ) : me && pickPosition ? (
          /* ---- position picker (multi-position employees) ---- */
          <div className="flex flex-col items-center rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center" data-testid="kiosk-position-pick">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0033ff]/20 text-[#5b7bff]">
              <UserRound className="h-7 w-7" />
            </div>
            <div className="mt-3 text-sm text-neutral-400">Hi {me.first_name || me.name}</div>
            <div className="text-lg font-bold">Which position today?</div>
            <div className="mt-5 grid w-full gap-2">
              {(me.positions || []).map((p) => (
                <Button key={p.department} disabled={busy}
                  onClick={() => { setPickPosition(false); doAction("punch", { position: p.department }); }}
                  data-testid={`kiosk-position-${p.department}`}
                  className="h-16 w-full flex-col bg-emerald-500 text-base font-bold text-black hover:bg-emerald-400">
                  {p.department}
                </Button>
              ))}
            </div>
            {error && <div className="mt-4 w-full rounded bg-red-500/15 px-3 py-2 text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
            <button onClick={() => setPickPosition(false)} className="mt-5 text-xs text-neutral-500 underline hover:text-neutral-300" data-testid="kiosk-position-cancel">Cancel</button>
          </div>
        ) : me ? (
          <div className="flex flex-col items-center rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center" data-testid="kiosk-welcome">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0033ff]/20 text-[#5b7bff]">
              <UserRound className="h-7 w-7" />
            </div>
            <div className="mt-3 text-sm text-neutral-400">Welcome</div>
            <div className="text-2xl font-bold" data-testid="kiosk-welcome-name">{me.name}</div>
            <div className="mt-2 text-sm">
              {me.on_break ? (
                <span className="text-blue-400">On lunch break{me.break_start ? ` since ${fmtT(me.break_start, tz)}` : ""}</span>
              ) : me.status === "in" ? (
                <span className="text-emerald-400">You are clocked IN{me.since ? ` since ${fmtT(me.since, tz)}` : ""}</span>
              ) : (
                <span className="text-neutral-400">You are clocked OUT</span>
              )}
              {me.status === "in" && !me.on_break && me.break_minutes > 0 && (
                <span className="text-neutral-500"> · {me.break_minutes} min lunch logged</span>
              )}
            </div>
            {me.status === "in" && me.current_position && (
              <div className="mt-1 text-xs uppercase tracking-wide text-[#5b7bff]" data-testid="kiosk-current-position">Working: {me.current_position}</div>
            )}
            {me.period_hours != null && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-sm font-semibold" data-testid="kiosk-period-hours">
                <span className="rounded-md bg-emerald-500/10 px-3 py-1.5 text-emerald-400">{me.period_hours} hrs this period</span>
                {me.last_period_hours != null && (
                  <span className="rounded-md bg-neutral-800 px-3 py-1.5 text-neutral-300" data-testid="kiosk-last-period-hours">{me.last_period_hours} hrs last period</span>
                )}
              </div>
            )}
            <button onClick={viewHours} disabled={hoursBusy} data-testid="kiosk-view-hours-btn"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#5b7bff] underline underline-offset-4 hover:text-[#8aa0ff] disabled:opacity-50">
              <Clock className="h-4 w-4" /> {hoursBusy ? "Loading…" : "View my hours & verify"}
            </button>
            {me.status === "out" && me.handbook_required && (
              <div className="mt-3 rounded-md bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-400" data-testid="kiosk-handbook-notice">
                Please review &amp; sign the Employee Handbook to clock in
              </div>
            )}
            {me.status === "out" && !me.handbook_required && me.reviews_required && (
              <div className="mt-3 rounded-md bg-amber-500/15 px-4 py-2 text-sm font-semibold text-amber-400" data-testid="kiosk-review-notice">
                You have {me.pending_reviews?.length || 1} review{(me.pending_reviews?.length || 1) > 1 ? "s" : ""} to read &amp; acknowledge before clocking in
              </div>
            )}

            {me.status === "out" ? (
              <Button onClick={clockIn} disabled={busy} data-testid="kiosk-punch-btn"
                className="mt-6 h-16 w-full bg-emerald-500 text-lg font-bold text-black hover:bg-emerald-400">
                <LogIn className="mr-2 h-5 w-5" /> {busy ? "Please wait…" : (me.handbook_required ? "Review Handbook & Clock In" : me.reviews_required ? "Read Review & Clock In" : "Clock In")}
              </Button>
            ) : me.on_break ? (
              <>
                <Button onClick={() => doAction("break")} disabled={busy} data-testid="kiosk-break-btn"
                  className="mt-6 h-16 w-full bg-blue-500 text-lg font-bold text-white hover:bg-blue-400">
                  <Coffee className="mr-2 h-5 w-5" /> {busy ? "Please wait…" : "End Lunch"}
                </Button>
                <Button onClick={() => doAction("punch")} disabled={busy} variant="outline" data-testid="kiosk-punch-btn"
                  className="mt-3 h-12 w-full border-neutral-700 bg-transparent text-neutral-200 hover:bg-neutral-800 hover:text-white">
                  <LogOut className="mr-2 h-4 w-4" /> Clock Out
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => doAction("punch")} disabled={busy} data-testid="kiosk-punch-btn"
                  className="mt-6 h-16 w-full bg-amber-500 text-lg font-bold text-black hover:bg-amber-400">
                  <LogOut className="mr-2 h-5 w-5" /> {busy ? "Please wait…" : "Clock Out"}
                </Button>
                <Button onClick={() => doAction("break")} disabled={busy} variant="outline" data-testid="kiosk-break-btn"
                  className="mt-3 h-12 w-full border-neutral-700 bg-transparent text-neutral-200 hover:bg-neutral-800 hover:text-white">
                  <Coffee className="mr-2 h-4 w-4" /> Start Lunch
                </Button>
              </>
            )}

            {error && <div className="mt-4 w-full rounded bg-red-500/15 px-3 py-2 text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
            {isHK && (
              <Button onClick={() => openRooms(false)} disabled={busy} variant="outline" data-testid="kiosk-rooms-btn"
                className="mt-4 h-12 w-full border-neutral-700 bg-transparent text-neutral-200 hover:bg-neutral-800 hover:text-white">
                <BedDouble className="mr-2 h-4 w-4" /> Update Rooms
              </Button>
            )}
            {isExecMe && (
              <Button onClick={() => openRooms(true)} disabled={busy} variant="outline" data-testid="kiosk-assign-btn"
                className="mt-3 h-12 w-full border-emerald-700/60 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200">
                <UserRound className="mr-2 h-4 w-4" /> Assign Rooms
              </Button>
            )}
            <button onClick={switchUser} className="mt-5 text-xs text-neutral-500 underline hover:text-neutral-300" data-testid="kiosk-switch-btn">
              Not {me.first_name || "you"}? Switch user
            </button>
          </div>
        ) : loginMode === "ssn" ? (
          /* ---- SSN fallback login (no phone) ---- */
          <div data-testid="kiosk-ssn-login">
            <div className="mb-3 text-center text-sm text-neutral-400">Enter your last name and the last 4 digits of your SSN</div>
            <label className="mb-1 block text-xs uppercase tracking-wide text-neutral-500">Last name</label>
            <Input
              value={lastName}
              onChange={(e) => { setError(""); setLastName(e.target.value); }}
              placeholder="Last name"
              data-testid="kiosk-lastname-input"
              className="mb-4 h-14 border-neutral-800 bg-neutral-900 text-lg text-white placeholder:text-neutral-600"
            />
            <label className="mb-1 block text-xs uppercase tracking-wide text-neutral-500">Last 4 of SSN</label>
            <Input
              value={ssn4}
              onChange={(e) => { setError(""); setSsn4(e.target.value.replace(/\D/g, "").slice(0, 4)); }}
              inputMode="numeric"
              placeholder="••••"
              data-testid="kiosk-ssn-input"
              className="mb-4 h-14 border-neutral-800 bg-neutral-900 text-center font-mono text-2xl tracking-[0.5em] text-white placeholder:text-neutral-600"
            />
            {error && <div className="mb-3 rounded bg-red-500/15 px-3 py-2 text-center text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
            <Button onClick={submitSsn} disabled={busy} data-testid="kiosk-ssn-continue-btn"
              className="h-16 w-full bg-[#0033ff] text-lg font-bold hover:bg-[#0028cc]">
              <Clock className="mr-2 h-5 w-5" />
              {busy ? "Please wait…" : "Continue"}
            </Button>
            <button onClick={() => { setLoginMode("phone"); setError(""); }} className="mt-4 block w-full text-center text-xs text-neutral-500 underline hover:text-neutral-300" data-testid="kiosk-use-phone-btn">
              Use my phone number instead
            </button>
          </div>
        ) : (
          /* ---- first-time phone entry (binds this device) ---- */
          <>
            <div className="mb-3 text-center text-sm text-neutral-400">Enter your phone number to get started on this phone</div>
            <div className="mb-4 flex h-16 items-center justify-center rounded-lg border border-neutral-800 bg-neutral-900 font-mono text-3xl tabular-nums" data-testid="kiosk-phone-display">
              {fmtPhone(phone) || <span className="text-neutral-600">Phone #</span>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => press(k)}
                  data-testid={`kiosk-key-${k}`}
                  className="flex h-16 items-center justify-center rounded-lg border border-neutral-800 bg-neutral-900 text-2xl font-semibold transition-colors hover:bg-neutral-800 active:bg-neutral-700"
                >
                  {k === "del" ? <Delete className="h-6 w-6" /> : k === "clear" ? <span className="text-sm uppercase tracking-wide text-neutral-400">Clear</span> : k}
                </button>
              ))}
            </div>
            {error && <div className="mt-4 rounded bg-red-500/15 px-3 py-2 text-center text-sm text-red-400" data-testid="kiosk-error">{error}</div>}
            <Button
              onClick={submitPhone}
              disabled={busy}
              data-testid="kiosk-continue-btn"
              className="mt-4 h-16 w-full bg-[#0033ff] text-lg font-bold hover:bg-[#0028cc]"
            >
              <Clock className="mr-2 h-5 w-5" />
              {busy ? "Please wait…" : "Continue"}
            </Button>
            <button onClick={() => { setLoginMode("ssn"); setError(""); }} className="mt-4 block w-full text-center text-xs text-neutral-500 underline hover:text-neutral-300" data-testid="kiosk-no-phone-btn">
              No phone? Use Last Name + last 4 of SSN
            </button>
            <p className="mt-3 text-center text-xs text-neutral-600">We'll remember you on this phone so next time it's one tap.</p>
          </>
        )}
      </div>
      <div className="mt-auto flex flex-col items-center gap-2 pt-8">
        <button onClick={toggleStation} data-testid="kiosk-station-toggle"
          className={`rounded-full px-3 py-1 text-[0.6rem] uppercase tracking-[0.15em] transition-colors ${station ? "bg-emerald-500/15 text-emerald-400" : "text-neutral-700 hover:text-neutral-500"}`}>
          {station ? "● Front-desk station (location check off)" : "Set as front-desk station"}
        </button>
        <div className="text-[0.6rem] uppercase tracking-[0.2em] text-neutral-700">GCG Hotels · Operations</div>
      </div>
    </div>
  );
}
